import { useCallback, useEffect, useRef, useState } from 'react';
import type { SpectatorMatch, SpectatorPage } from '@aiscoreboard/contracts';
import { Flag } from '../components/CountryPicker';
import { PlayIcon } from '../components/Icons';
import { LanguageSelect } from '../components/LanguageSelect';
import { fetchSpectatorMatches } from '../lib/api';
import { useT } from '../lib/i18n';
import { pullToRefreshMessage, usePullToRefresh } from '../lib/usePullToRefresh';
import { safeYouTubeWatchUrl } from '../lib/youtube';

export function SpectatorScreen({ onBack }: { onBack: () => void }) {
  const t = useT();
  const [page, setPage] = useState<SpectatorPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef<{ controller: AbortController; promise: Promise<void> } | null>(null);
  const refresh = useCallback((): Promise<void> => {
    if (request.current) return request.current.promise;
    const controller = new AbortController();
    setLoading(true);
    const promise = (async () => {
      try {
        const result = await fetchSpectatorMatches(controller.signal);
        if (!controller.signal.aborted) {
          setPage(result);
          setError(null);
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (request.current?.controller === controller) request.current = null;
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    request.current = { controller, promise };
    return promise;
  }, []);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async (): Promise<void> => {
      await refresh();
      if (!cancelled) timer = setTimeout(() => void poll(), 5000);
    };
    void poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      request.current?.controller.abort();
      request.current = null;
    };
  }, [refresh]);
  const pull = usePullToRefresh({ onRefresh: refresh });
  const pullMessage = pullToRefreshMessage(pull.pullDistance, pull.refreshing);

  return <main className="relative h-full w-full overflow-y-auto p-4 sm:p-6"
    onTouchStart={pull.onTouchStart} onTouchMove={pull.onTouchMove} onTouchEnd={pull.onTouchEnd}>
    <div className="mx-auto max-w-6xl space-y-6">
      {(pull.pullDistance > 0 || pull.refreshing) && <p role="status" className="text-center text-sm text-white/60">
        {t(pullMessage === 'refreshing' ? 'common.loading' : pullMessage === 'release' ? 'common.releaseToRefresh' : 'common.pullToRefresh')}
      </p>}
      <header className="flex flex-wrap items-center gap-3 max-sm:grid max-sm:grid-cols-[auto_1fr]">
        <button type="button" onClick={onBack} className="btn-ghost px-3 py-2 text-sm">{t('common.back')}</button>
        <h1 className="mr-auto text-2xl font-bold">{t('spectator.title')}</h1>
        <button type="button" onClick={() => void refresh()} disabled={loading}
          className="btn-ghost px-3 py-2 text-sm disabled:opacity-40">{t('spectator.refresh')}</button>
        <LanguageSelect />
      </header>
      <p className="text-sm text-white/60 max-sm:min-h-[3.75rem]">{t('spectator.hint')}</p>
      {error && <p role="alert" className="rounded border border-red-400/40 p-3 text-red-300">
        {t('spectator.failed')} {error}
      </p>}
      {!page && loading && <p role="status">{t('common.loading')}</p>}
      {page?.clubs.length === 0 && <p role="status" className="panel p-8 text-center text-white/60">{t('spectator.empty')}</p>}
      {page?.clubs.map(club => <section key={club.id} aria-label={club.name ?? t('spectator.unassigned')} className="space-y-3">
        <h2 className="flex items-center gap-3 text-xl font-bold">
          <span className="min-w-0 break-words">{club.name ?? t('spectator.unassigned')}</span>
          <span className="rounded-full bg-white/10 px-3 py-1 text-sm text-white/60">{club.matches.length}</span>
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          {club.matches.map(match => <MatchCard key={match.id} match={match} fresh={!error} />)}
        </div>
      </section>)}
    </div>
  </main>;
}

function MatchCard({ match, fresh }: { match: SpectatorMatch; fresh: boolean }) {
  const t = useT();
  const watchUrl = fresh ? safeYouTubeWatchUrl(match.watchUrl) : null;
  return <article className="panel min-w-0 space-y-4 p-4" aria-label={match.players.join(' vs ')}>
    <header className="flex flex-wrap items-center justify-between gap-2 text-sm">
      <span className="min-w-0 break-words font-bold text-white/75">{match.tableName ?? t('spectator.match')}</span>
      <span className="text-white/60">{t('match.bestOf', { n: match.bestOf })}
        {match.timeLimitMinutes !== null && ` · ${t('spectator.minutes', { n: match.timeLimitMinutes })}`}</span>
    </header>
    <table className="w-full table-fixed text-left">
      <thead className="text-xs text-white/50">
        <tr><th className="pb-2 font-normal">{t('spectator.player')}</th>
          <th className="w-16 pb-2 text-center font-normal">{t('spectator.points')}</th>
          <th className="w-16 pb-2 text-center font-normal">{t('spectator.frames')}</th></tr>
      </thead>
      <tbody>
        {([0, 1] as const).map(seat => <tr key={seat} className="border-t border-white/10">
          <td className="py-3 pr-2">
            <span className="flex min-w-0 items-center gap-2">
              {match.countries[seat] && <Flag code={match.countries[seat]} />}
              <span className="min-w-0 break-words font-semibold">{match.players[seat]}</span>
            </span>
          </td>
          <td className="text-center text-2xl font-bold tabular-nums text-cyan-300">{match.scores[seat]}</td>
          <td className="text-center text-xl font-bold tabular-nums">{match.framesWon[seat]}</td>
        </tr>)}
      </tbody>
    </table>
    <footer className="flex flex-wrap items-center justify-between gap-3">
      <span className="text-xs text-white/50">{t('spectator.frame', { n: match.frameNumber })}
        {match.status === 'frame_over' && ` · ${t('spectator.interval')}`}</span>
      {watchUrl && <a href={watchUrl} target="_blank" rel="noopener noreferrer"
        aria-label={t('spectator.watchMatch', { a: match.players[0], b: match.players[1] })}
        className="inline-flex items-center gap-2 rounded-full bg-green-600 px-4 py-2 text-sm font-bold text-white hover:bg-green-500">
        <PlayIcon className="h-4 w-4" />{t('spectator.watch')}
      </a>}
    </footer>
  </article>;
}
